<script lang="ts">
  import { onDestroy } from 'svelte'
  import { SignalingClient, defaultSignalUrl, type SignalingStatus } from '../lib/signaling'
  import { MAX_VIEWER_CAP, PROTOCOL_VERSION, type ClientMessage } from '../lib/protocol'

  /**
   * The protocol smoke test.
   *
   * Phase 0's acceptance check lives here: it proves the WebSocket transport, the
   * JSON framing and the protocol version handshake against a real server,
   * without needing the capture pipeline or a second machine. It stays useful
   * afterwards as the place to watch raw messages when something in the media
   * path misbehaves.
   */

  let url = $state(defaultSignalUrl())
  let status = $state<SignalingStatus>('idle')
  let detail = $state('')
  let rttMs = $state<number | null>(null)
  let lastError = $state<string | null>(null)
  let joinCode = $state('')
  let log = $state<{ at: number; dir: 'in' | 'out' | 'sys'; text: string }[]>([])

  // `$state.raw` rather than `$state`: the template reads `client.isOpen`, so the
  // variable has to be reactive, but there is no reason to deep-proxy a class
  // instance that owns a WebSocket.
  let client = $state.raw<SignalingClient | null>(null)

  const MAX_LOG_LINES = 200

  function append(dir: 'in' | 'out' | 'sys', text: string) {
    const next = log.concat([{ at: Date.now(), dir, text }])
    log = next.length > MAX_LOG_LINES ? next.slice(next.length - MAX_LOG_LINES) : next
  }

  function timestamp(at: number): string {
    return new Date(at).toLocaleTimeString(undefined, { hour12: false })
  }

  function connect() {
    lastError = null
    client?.close()

    client = new SignalingClient(url, {
      handlers: {
        onStatus: (next, reason) => {
          status = next
          detail = reason ?? ''
          append('sys', `status: ${next}${reason ? ` (${reason})` : ''}`)
        },
        onOpen: () => append('sys', `connected to ${url} (protocol ${PROTOCOL_VERSION})`),
        onMessage: (message) => append('in', JSON.stringify(message)),
        onError: (message) => {
          lastError = message
          append('sys', `error: ${message}`)
        },
      },
    })

    append('sys', `connecting to ${url}…`)
    client.connect()
  }

  function disconnect() {
    client?.close()
    client = null
    rttMs = null
  }

  function send(message: ClientMessage, label: string) {
    if (!client?.isOpen) {
      lastError = 'Not connected. Press Connect first.'
      return
    }
    client.send(message)
    append('out', `${label}: ${JSON.stringify(message)}`)
  }

  async function ping() {
    if (!client?.isOpen) {
      lastError = 'Not connected. Press Connect first.'
      return
    }
    const nonce = Math.floor(Math.random() * 1_000_000)
    append('out', `ping: ${JSON.stringify({ type: 'ping', nonce })}`)
    try {
      rttMs = await client.ping()
    } catch (error) {
      lastError = error instanceof Error ? error.message : String(error)
    }
  }

  function clearLog() {
    log = []
  }

  onDestroy(() => client?.close())
</script>

<div class="page stack">
  <div>
    <h1>Protocol debug</h1>
    <p class="muted">
      Talks to the signaling server directly. Nothing here touches media: it checks that the
      transport, the JSON framing and the version handshake work before any capture code is
      involved.
    </p>
  </div>

  <div class="card stack">
    <div class="field">
      <label for="url">Signaling URL</label>
      <input id="url" bind:value={url} class="mono" spellcheck="false" />
    </div>

    <div class="row">
      {#if client?.isOpen}
        <button type="button" onclick={disconnect}>Disconnect</button>
      {:else}
        <button type="button" class="primary" onclick={connect}>Connect</button>
      {/if}

      <button type="button" onclick={ping} disabled={!client?.isOpen}>Ping</button>

      <span
        class="badge"
        class:badge--ok={status === 'open'}
        class:badge--warn={status === 'reconnecting'}
      >
        <span class="dot"></span>
        {status}
      </span>

      {#if rttMs !== null}
        <span class="faint mono">RTT {rttMs.toFixed(1)} ms</span>
      {/if}
      {#if detail}
        <span class="faint">{detail}</span>
      {/if}
    </div>

    {#if lastError}
      <div class="notice notice--error">{lastError}</div>
    {/if}
  </div>

  <div class="card stack">
    <h2>Send a message</h2>

    <div class="row">
      <button
        type="button"
        onclick={() =>
          send(
            {
              type: 'host_create_room',
              name: 'debug-host',
              viewer_cap: 4,
              password: undefined,
              protocol_version: PROTOCOL_VERSION,
            },
            'host_create_room',
          )}
        disabled={!client?.isOpen}
      >
        host_create_room
      </button>
      <span class="faint"
        >Expect the current server to refuse this and say which phase lands it.</span
      >
    </div>

    <div class="row">
      <input
        bind:value={joinCode}
        placeholder="ABCDEF"
        class="mono"
        style="max-width: 140px"
        spellcheck="false"
      />
      <button
        type="button"
        onclick={() =>
          send(
            {
              type: 'viewer_join',
              code: joinCode.trim().toUpperCase(),
              name: 'debug-viewer',
              protocol_version: PROTOCOL_VERSION,
            },
            'viewer_join',
          )}
        disabled={!client?.isOpen || joinCode.trim() === ''}
      >
        viewer_join
      </button>
    </div>

    <div class="row">
      <button
        type="button"
        onclick={() => send({ type: 'leave_room' }, 'leave_room')}
        disabled={!client?.isOpen}
      >
        leave_room
      </button>
      <span class="faint">Max viewer cap accepted by the protocol: {MAX_VIEWER_CAP}.</span>
    </div>
  </div>

  <div class="card stack">
    <div class="spread">
      <h2 style="margin: 0">Messages</h2>
      <button type="button" class="ghost" onclick={clearLog} disabled={log.length === 0}
        >Clear</button
      >
    </div>

    <div class="log">
      {#each log as line (line.at + line.text)}
        <div class="log__line">
          <span class="log__time">{timestamp(line.at)}</span>
          <span
            style="color: {line.dir === 'in'
              ? 'var(--ok)'
              : line.dir === 'out'
                ? 'var(--accent)'
                : 'var(--text-faint)'}"
          >
            {line.dir === 'in' ? '←' : line.dir === 'out' ? '→' : '·'}
          </span>
          <span>{line.text}</span>
        </div>
      {:else}
        <span class="faint">Nothing yet. Press Connect.</span>
      {/each}
    </div>
  </div>
</div>
